import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, Banner } from '@shopify/polaris';
import { Provider as AppBridgeProvider, useAppBridge } from '@shopify/app-bridge-react';
import { useEffect } from 'react';
import enTranslations from '@shopify/polaris/locales/en.json';
import AppLayout from './components/Layout/AppLayout';
import VendorManagement from './pages/VendorManagement';
import ManualExport from './pages/ManualExport';
import ExportHistory from './pages/ExportHistory';
import Settings from './pages/Settings';
import UnauthorizedAccess from './components/UnauthorizedAccess';
import { setAppBridge } from './utils/api';

// Check if authentication is disabled for development
const AUTH_DISABLED = import.meta.env.VITE_DISABLE_SHOPIFY_AUTH === 'true';

// Inner component that has access to App Bridge context
function AppContent() {
  const app = AUTH_DISABLED ? null : useAppBridge();
  
  // Set App Bridge instance for API utility
  useEffect(() => {
    if (app) {
      setAppBridge(app);
    }
  }, [app]);

  return (
    <Router>
      {AUTH_DISABLED && (
        <div style={{ padding: '1rem' }}>
          <Banner tone="warning">
            <p><strong>Development Mode:</strong> Shopify authentication is disabled. This app is running outside of Shopify's iframe.</p>
          </Banner>
        </div>
      )}
      <Routes>
        <Route path="/" element={<AppLayout />}>
          <Route index element={<Navigate to="/vendors" replace />} />
          <Route path="vendors" element={<VendorManagement />} />
          <Route path="manual-export" element={<ManualExport />} />
          <Route path="export-history" element={<ExportHistory />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  // If authentication is disabled, skip App Bridge entirely
  if (AUTH_DISABLED) {
    console.warn('⚠️  Running in DEVELOPMENT MODE without Shopify App Bridge');
    return (
      <AppProvider i18n={enTranslations}>
        <AppContent />
      </AppProvider>
    );
  }
  
  // Extract Shopify parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY;
  
  // Validate required environment variables
  if (!apiKey) {
    console.error('❌ VITE_SHOPIFY_API_KEY is not set in environment variables');
    console.error('This variable must be set at build time in docker-compose-prod.yml');
    console.error('Check your .env file and rebuild: docker-compose -f docker-compose-prod.yml build web');
    return (
      <AppProvider i18n={enTranslations}>
        <UnauthorizedAccess />
      </AppProvider>
    );
  }
  
  // If no host parameter, show unauthorized access page
  // This prevents App Bridge initialization errors when accessing directly
  if (!host) {
    return (
      <AppProvider i18n={enTranslations}>
        <UnauthorizedAccess />
      </AppProvider>
    );
  }
  
  // App Bridge configuration
  const appBridgeConfig = {
    apiKey: apiKey,
    host: host,
    forceRedirect: false
  };

  return (
    <AppBridgeProvider config={appBridgeConfig}>
      <AppProvider i18n={enTranslations}>
        <AppContent />
      </AppProvider>
    </AppBridgeProvider>
  );
}

export default App;
